import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// --- Shell UI
import ReportModal from '../../../components/report/ReportModal';

// --- UI atoms/molecules (split from GroupDetail)
import LoadingSpinner from '../groupPageComponents/LoadingSpinner';
import { ErrorState as ErrorDisplay } from '../../../components/ui';
import PermissionGate from '../groupPageComponents/PermissionGate';
import GroupPosts from '../groupPageComponents/GroupPosts';
import GroupAbout from '../groupPageComponents/GroupAbout';
import MemberList from '../groupPageComponents/GroupMemberList';
import DeleteGroupModal from '../groupPageComponents/DeleteGroupModal';
import InviteMembersModal from '../groupPageComponents/InviteMembersModal';
import GroupPendingMembers from '../groupPageComponents/GroupPendingMembers';
import StatCard from '../groupPageComponents/StatCard';
import TabButton from '../groupPageComponents/TabButton';
import GroupHeader from '../groupPageComponents/GroupHeader';
import GroupStocksSection from '../groupPageComponents/GroupStockAssociation';
import GroupPricing from '../groupPageComponents/GroupPricing';
import GroupAnalytics from '../groupPageComponents/GroupAnalytics';
import { parseUtcDate } from '../../../utils/parseUtcDate';

// --- Icons
import {
  Users,
  MessageCircle,
  Shield,
  TrendingUp,
  UserPlus,
  Trash2,
  Clock,
  ArrowLeft,
  DollarSign,
  BarChart3,
  CheckCircle,
} from 'lucide-react';

// --- Groups (CRUD/members/messages)
import {
  fetchGroupById,
  fetchGroupMembers,
  fetchGroupMessages,
  removeMemberFromGroup,
  updateGroup,
  deleteGroup,
  joinGroup,
  leaveGroup,
  updateMemberRole,
  transferOwnership,
  muteMember,
  unmuteMember,
  fetchGroupDisplayPicture,
  upsertGroupDisplayPicture,
  updateGroupBackgroundTheme,
} from '../../../redux/userGroups/groups/userGroupies';

// --- Group posts
import {
  getGroupPosts, createGroupPost,
  likeGroupPost, unlikeGroupPost,
  bullGroupPost, unbullGroupPost,
  bearGroupPost, unbearGroupPost,
  createGroupComment, deleteGroupComment,
  likeGroupComment, unlikeGroupComment,
} from '../../../redux/userGroups/groupPost/userGroupesPosts';
import { fetchMySavedPostIdsThunk, savePostThunk, unsavePostThunk } from '../../../redux/savedPosts/savedPostsActions';

// --- Cleanup / state resets
import { resetAllErrors, clearCurrentGroup } from '../../../redux/userGroups/groups/groupReducer';
import { resetGroupPosts } from '../../../redux/userGroups/groupPost/groupPostSlice';
import { fireConfettiBurst } from '../../../utils/celebration';

// 2026-07-19: owners/admins/moderators can post + mute; plain members can
// only comment. Mirrors the backend's crud_groupies.ROLE_RANK. Module-level
// (not per-render) so it's a stable reference for the useMemo below.
const ROLE_RANK = { owner: 3, admin: 2, moderator: 1, member: 0 };

const GroupDetailPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // route param -> numeric id
  const { groupId: groupIdParam } = useParams();
  const groupId = Number(groupIdParam);

  // local UI state
  // GroupListPage's "Edit Group" action deep-links here via route state
  // (same destination "Manage Group" already reaches internally, one click
  // earlier) -- mirrors this app's existing focusTaskId/prefillStock pattern.
  const [activeTab, setActiveTab] = useState(location.state?.openTab || 'posts');
  const [showReportModal, setShowReportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showJoinedToast, setShowJoinedToast] = useState(false);

  // auth
  const { user: currentUser } = useSelector((s) => s.auth || {});

  // groups slice
  const {
    groups = [],
    currentGroup,
    members = [],
    status = 'idle',
    error = null,
    updateGroupStatus = 'idle',
    updateGroupError = null,
    deleteGroupStatus = 'idle',
    deleteGroupError = null,
    joinGroupStatus = 'idle',
    fetchMembersStatus = 'idle',
    removeMemberError = null,
    updateRoleError = null,
    transferOwnershipError = null,
    displayPicture = null,
    updateThemeStatus = 'idle',
    updateThemeError = null,
    upsertPictureError = null,
    approveMemberError = null,
    rejectMemberError = null,
    muteError = null,
  } = useSelector((s) => s.groups || {});

  // posts slice
  const {
    posts = [],
    hasMore: postsHasMore = false,
    status: postsStatus = 'idle',
    error: postsError = null,
  } = useSelector((s) => s.groupPosts || {});

  const postsCount = posts?.length ?? 0;

  // `members` includes pending/rejected rows now that "pending" is a real,
  // surfaced state (see GroupPendingMembers below) -- counts and the main
  // roster grid should only reflect actually-approved members.
  const approvedMembers = useMemo(
    () => (members || []).filter((m) => (m.status ?? 'approved') === 'approved'),
    [members]
  );
  const pendingMembers = useMemo(
    () => (members || []).filter((m) => m.status === 'pending'),
    [members]
  );

  // choose group (prefer currentGroup, fallback to list)
  const group = useMemo(
    () => currentGroup || groups.find((g) => g?.id === groupId),
    [currentGroup, groups, groupId]
  );

  // permissions
  const { isOwner, isMember, isAdmin, canCreatePost, canMute, canEditGroupTheme, viewerRole, myMembership, isMemberMuted } = useMemo(() => {
    const memberRec = members?.find((m) => (m.user_id ?? m.id) === currentUser?.id);
    const owner =
      !!(group && currentUser &&
        (group.owner_user_id === currentUser.id ||
          group.owner?.id === currentUser.id ||
          group.created_by === currentUser.id));
    const effectiveRole = owner ? 'owner' : memberRec?.role;
    const rankAtLeastModerator = (ROLE_RANK[effectiveRole] ?? -1) >= ROLE_RANK.moderator;
    // Mirrors the backend's lazy-expiry check (crud_groupies.is_member_muted):
    // a timed mute whose muted_until has passed reads as not-muted here even
    // though the is_muted flag itself isn't cleared until the next mute/unmute.
    const isMemberMuted = !!memberRec?.is_muted &&
      (!memberRec?.muted_until || parseUtcDate(memberRec.muted_until) > new Date());
    return {
      isOwner: owner,
      isMember: !!memberRec,
      isAdmin: owner || memberRec?.role === 'admin',
      canCreatePost: rankAtLeastModerator,
      canMute: rankAtLeastModerator,
      canEditGroupTheme: rankAtLeastModerator,
      viewerRole: effectiveRole,
      myMembership: memberRec,
      isMemberMuted,
    };
  }, [group, members, currentUser]);

  // gating certain sections only for member privileges etc
  const canViewMembers = isOwner || isAdmin || (group?.privacy === 'public' && isMember);
  const canViewPosts   = isOwner || isAdmin || isMember;

  // initial fetch + cleanup
  useEffect(() => {
    if (!Number.isFinite(groupId)) return;

    dispatch(fetchGroupById(groupId));
    dispatch(fetchGroupMembers(groupId));
    dispatch(getGroupPosts({ groupId, skip: 0, limit: 10 }));
    dispatch(fetchGroupMessages({ groupId })); // optional stream
    dispatch(fetchGroupDisplayPicture(groupId));

    return () => {
      dispatch(clearCurrentGroup());
      dispatch(resetAllErrors());
      dispatch(resetGroupPosts());
    };
  }, [dispatch, groupId]);

  // Redirects a non-member away from a private group's Posts tab. Was
  // keyed on `isMember` alone, which is derived from the async-fetched
  // `members` list -- on first mount `isMember` reads false for everyone,
  // including the group's own owner, until that fetch resolves, tripping
  // this redirect before it can find out the real answer (and never
  // reversing once it does, since this effect only ever transitions
  // posts->about). Two independent guards: `isOwner` doesn't depend on the
  // members fetch at all (derived straight from `group.owner_user_id`), so
  // an owner is never subject to this race in the first place; everyone
  // else now waits for `fetchMembersStatus` to actually resolve before the
  // "not a member" reading is trusted enough to act on.
  useEffect(() => {
    if (
      activeTab === 'posts' &&
      group?.privacy === 'private' &&
      !isOwner &&
      !isMember &&
      fetchMembersStatus === 'succeeded'
    ) {
      setActiveTab('about');
    }
  }, [group?.privacy, isOwner, isMember, fetchMembersStatus, activeTab]);

  // retry
  const handleRetry = useCallback(() => {
    if (!Number.isFinite(groupId)) return;
    dispatch(fetchGroupById(groupId));
    dispatch(fetchGroupMembers(groupId));
    dispatch(getGroupPosts({ groupId, skip: 0, limit: 10 }));
  }, [dispatch, groupId]);

  // refresh group data after stock association changes
  const handleStockAssociationComplete = useCallback(() => {
    if (!Number.isFinite(groupId)) return;
    dispatch(fetchGroupById(groupId));
  }, [dispatch, groupId]);

  // back button
  const handleBack = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/groupies');
    }
  };

  // actions (wired to Redux)
  const onJoinGroup = useCallback(async () => {
    if (!Number.isFinite(groupId)) return;
    const r = await dispatch(joinGroup(groupId));
    if (r.meta.requestStatus === 'fulfilled') {
      dispatch(fetchGroupMembers(groupId));
      fireConfettiBurst();
      setShowJoinedToast(true);
      setTimeout(() => setShowJoinedToast(false), 3500);
    }
  }, [dispatch, groupId]);

  const onLeaveGroup = useCallback(async () => {
    if (!Number.isFinite(groupId) || isOwner) return;
    if (!window.confirm(t('groups.detailPage.confirmLeaveGroup'))) return;
    const r = await dispatch(leaveGroup(groupId));
    if (r.meta.requestStatus === 'fulfilled') navigate('/groupies');
  }, [dispatch, groupId, isOwner, navigate, t]);

  const onUpdateGroup = useCallback(async (groupData) => {
    if (!Number.isFinite(groupId) || !isOwner) return;
    await dispatch(updateGroup({ groupId, groupData }));
  }, [dispatch, groupId, isOwner]);

  const onSelectGroupAvatar = useCallback(async (pictureUrl) => {
    if (!Number.isFinite(groupId) || !isOwner) return;
    await dispatch(upsertGroupDisplayPicture({ groupId, pictureUrl }));
  }, [dispatch, groupId, isOwner]);

  const onSelectGroupBackgroundTheme = useCallback(async (backgroundTheme) => {
    if (!Number.isFinite(groupId) || !canEditGroupTheme) return;
    await dispatch(updateGroupBackgroundTheme({ groupId, backgroundTheme }));
  }, [dispatch, groupId, canEditGroupTheme]);

  const onDeleteGroup = useCallback(async () => {
    if (!Number.isFinite(groupId) || !isOwner) return;
    const r = await dispatch(deleteGroup(groupId));
    if (r.meta.requestStatus === 'fulfilled') navigate('/groupies');
  }, [dispatch, groupId, isOwner, navigate]);

  const onRemoveMember = useCallback(async (userId) => {
    if (!Number.isFinite(groupId) || !(isOwner || isAdmin)) return;
    if (!window.confirm(t('groups.detailPage.confirmRemoveMember'))) return;
    const r = await dispatch(removeMemberFromGroup({ groupId, userId }));
    if (r.meta.requestStatus === 'fulfilled') dispatch(fetchGroupMembers(groupId));
  }, [dispatch, groupId, isOwner, isAdmin, t]);

  const onPromoteMember = useCallback(async (userId) => {
    if (!Number.isFinite(groupId) || !isOwner) return;
    const r = await dispatch(updateMemberRole({ groupId, userId, newRole: 'admin' }));
    if (r.meta.requestStatus === 'fulfilled') dispatch(fetchGroupMembers(groupId));
  }, [dispatch, groupId, isOwner]);

  const onDemoteMember = useCallback(async (userId) => {
    if (!Number.isFinite(groupId) || !isOwner) return;
    const r = await dispatch(updateMemberRole({ groupId, userId, newRole: 'member' }));
    if (r.meta.requestStatus === 'fulfilled') dispatch(fetchGroupMembers(groupId));
  }, [dispatch, groupId, isOwner]);

  const onMuteMember = useCallback(async (userId) => {
    if (!Number.isFinite(groupId) || !canMute) return;
    const r = await dispatch(muteMember({ groupId, userId }));
    if (r.meta.requestStatus === 'fulfilled') dispatch(fetchGroupMembers(groupId));
  }, [dispatch, groupId, canMute]);

  const onUnmuteMember = useCallback(async (userId) => {
    if (!Number.isFinite(groupId) || !canMute) return;
    const r = await dispatch(unmuteMember({ groupId, userId }));
    if (r.meta.requestStatus === 'fulfilled') dispatch(fetchGroupMembers(groupId));
  }, [dispatch, groupId, canMute]);

  const onTransferOwnership = useCallback(async (userId) => {
    if (!Number.isFinite(groupId) || !isOwner) return;
    const targetMember = members.find((m) => (m.user_id ?? m.id) === userId);
    const targetName = targetMember?.username || t('groups.detailPage.thisMemberFallback');
    if (!window.confirm(t('groups.detailPage.confirmTransferOwnership', { targetName }))) return;
    const r = await dispatch(transferOwnership({ groupId, newOwnerUserId: userId }));
    if (r.meta.requestStatus === 'fulfilled') {
      dispatch(fetchGroupById(groupId));
      dispatch(fetchGroupMembers(groupId));
    }
  }, [dispatch, groupId, isOwner, members, t]);

  // posts handlers
  const onRetryPosts = () => {
    if (!Number.isFinite(groupId)) return;
    dispatch(getGroupPosts({ groupId, skip: 0, limit: 10 }));
  };

  const onLoadMorePosts = () => {
    if (!Number.isFinite(groupId)) return;
    if (postsHasMore && postsStatus !== 'loading') {
      dispatch(getGroupPosts({ groupId, skip: posts.length, limit: 10 }));
    }
  };

  // ✅ FIXED: destructure {title, content, evidence} from GroupPostComposer's onCreatePost call
  const onCreatePost = useCallback(async ({ title, content, evidence = [], disclosesPosition = false }) => {
    if (!Number.isFinite(groupId)) return;
    const r = await dispatch(createGroupPost({ groupId, title, content, evidence, disclosesPosition }));
    if (r.meta.requestStatus === 'rejected') {
      throw new Error(r.payload || 'Failed to create post');
    }
    return r;
  }, [dispatch, groupId]);

  // reactions — mirrors onCreatePost's dispatch pattern
  const onLike = useCallback(({ postId }) => dispatch(likeGroupPost({ postId })), [dispatch]);
  const onUnlike = useCallback(({ postId }) => dispatch(unlikeGroupPost({ postId })), [dispatch]);
  const onBull = useCallback(({ postId }) => dispatch(bullGroupPost({ postId })), [dispatch]);
  const onUnbull = useCallback(({ postId }) => dispatch(unbullGroupPost({ postId })), [dispatch]);
  const onBear = useCallback(({ postId }) => dispatch(bearGroupPost({ postId })), [dispatch]);
  const onUnbear = useCallback(({ postId }) => dispatch(unbearGroupPost({ postId })), [dispatch]);

  // saved posts (bookmark) -- .unwrap() so a real failure surfaces via
  // GroupPosts.jsx's own reactingKeys/console.warn, matching onLike/onBull's
  // fire-and-forget dispatch pattern is intentionally NOT mirrored here since
  // this one needs to report failure back to the caller for the UI to react.
  const savedGroupPostIds = useSelector((s) => s.savedPosts?.groupPostIds || []);
  useEffect(() => {
    dispatch(fetchMySavedPostIdsThunk());
  }, [dispatch]);
  const onSave = useCallback(({ groupPostId }) => dispatch(savePostThunk({ groupPostId })).unwrap(), [dispatch]);
  const onUnsave = useCallback(({ groupPostId }) => dispatch(unsavePostThunk({ groupPostId })).unwrap(), [dispatch]);

  // comments
  const onCreateComment = useCallback(
    ({ postId, content, parentCommentId }) => dispatch(createGroupComment({ postId, content, parentCommentId })),
    [dispatch]
  );
  const onDeleteComment = useCallback(
    ({ postId, commentId }) => dispatch(deleteGroupComment({ postId, commentId })),
    [dispatch]
  );
  const onLikeComment = useCallback(
    ({ postId, commentId }) => dispatch(likeGroupComment({ postId, commentId })),
    [dispatch]
  );
  const onUnlikeComment = useCallback(
    ({ postId, commentId }) => dispatch(unlikeGroupComment({ postId, commentId })),
    [dispatch]
  );

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: group?.name,
          text: group?.description,
          url: window.location.href,
        });
      } catch (e) {
        // user canceled share
      }
    } else {
      await navigator.clipboard.writeText(window.location.href);
      alert(t('groups.detailPage.groupLinkCopied'));
    }
  };

  // page-level gates
  if (status === 'loading' && !group) return <LoadingSpinner />;
  if (error) return <ErrorDisplay message={error} onRetry={handleRetry} />;
  if (!group) return <ErrorDisplay message={t('groups.detailPage.groupNotFound')} onRetry={handleRetry} />;

  // ✅ derive group stocks once here so we can pass to both GroupPosts and future consumers
  const groupStocks = group?.stocks || [];

  return (
    <div className="flex-1 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="p-4 sm:p-6">
          <div className="max-w-7xl mx-auto">
            {/* Back Button */}
            <div className="mb-6">
              <button
                onClick={handleBack}
                className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors duration-200 group"
              >
                <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform duration-200" />
                <span className="font-medium">{t('groups.detailPage.back')}</span>
              </button>
              </div>
            {/* Header */}
            <GroupHeader
              group={group}
              isOwner={isOwner}
              isMember={isMember}
              isAdmin={isAdmin}
              postsCount={canViewPosts ? postsCount : '—'}
              membersCount={canViewMembers ? approvedMembers.length : '—'}
              avatarUrl={displayPicture?.picture_url}
              onShare={handleShare}
              onEdit={() => setActiveTab('about')}
              onDelete={() => setShowDeleteModal(true)}
              onReport={() => setShowReportModal(true)}
              onLeave={onLeaveGroup}
              onJoin={onJoinGroup}
              onInvite={() => setShowInviteModal(true)}
              onSelectAvatar={onSelectGroupAvatar}
              canEditGroupTheme={canEditGroupTheme}
              onSelectBackgroundTheme={onSelectGroupBackgroundTheme}
              isSavingTheme={updateThemeStatus === 'loading'}
            />
            {/* Was captured in state (upsertPictureError/updateThemeError)
                but never rendered anywhere -- a failed avatar or theme
                change silently reverted on next fetch with no explanation. */}
            {(upsertPictureError || updateThemeError) && (
              <p className="text-sm text-red-500 dark:text-red-400 mt-2">
                {upsertPictureError || updateThemeError}
              </p>
            )}

            {/* Content */}
            {/* GroupHeader's title block is now normal-flow (negative
                margin, not absolute+bottom), so it already occupies real
                height here -- this only needs breathing room, not a fixed
                guess at how far the old absolutely-positioned block used
                to spill below the banner. */}
            <div className="pt-6 px-4 sm:px-8">
              <div className="max-w-7xl mx-auto">
                {(updateGroupError || deleteGroupError || removeMemberError || updateRoleError || transferOwnershipError) && (
                  <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 rounded-2xl p-4 space-y-1">
                    {updateGroupError && <p className="text-red-600 dark:text-red-400">{t('groups.detailPage.updateError', { error: updateGroupError })}</p>}
                    {deleteGroupError && <p className="text-red-600 dark:text-red-400">{t('groups.detailPage.deleteError', { error: deleteGroupError })}</p>}
                    {removeMemberError && <p className="text-red-600 dark:text-red-400">{t('groups.detailPage.removeMemberError', { error: removeMemberError })}</p>}
                    {updateRoleError && <p className="text-red-600 dark:text-red-400">{t('groups.detailPage.roleChangeError', { error: updateRoleError })}</p>}
                    {transferOwnershipError && <p className="text-red-600 dark:text-red-400">{t('groups.detailPage.transferOwnershipError', { error: transferOwnershipError })}</p>}
                  </div>
                )}

                {/* Join CTA */}
                {!isMember && group?.privacy === 'public' && (
                  <div className="mb-8 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-2xl p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-semibold text-blue-700 dark:text-blue-300 mb-2">{t('groups.detailPage.joinThisGroup')}</h3>
                        <p className="text-blue-700 dark:text-blue-200/80">
                          {t('groups.detailPage.joinCtaBody')}
                        </p>
                      </div>
                      <button
                        onClick={onJoinGroup}
                        disabled={joinGroupStatus === 'loading'}
                        className="flex-shrink-0 px-6 py-3 bg-gradient-to-r from-violet-500 to-blue-600 hover:from-violet-400 hover:to-blue-500 text-white rounded-xl flex items-center justify-center space-x-2 transition-all duration-300 disabled:opacity-50"
                      >
                        {joinGroupStatus === 'loading' ? (
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <UserPlus size={18} />
                        )}
                        <span>{joinGroupStatus === 'loading' ? t('groups.detailPage.joining') : t('groups.detailPage.joinGroup')}</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <StatCard
                    icon={Users}
                    label={t('groups.detailPage.totalMembers')}
                    value={canViewMembers ? approvedMembers.length : '—'}
                    color="blue"
                    onClick={() => canViewMembers && setActiveTab('members')}
                    clickable={canViewMembers}
                  />
                  <StatCard
                    icon={MessageCircle}
                    label={t('groups.detailPage.posts')}
                    value={canViewPosts ? postsCount : '—'}
                    color="green"
                    onClick={() => canViewPosts && setActiveTab('posts')}
                    clickable={canViewPosts}
                  />
                  <StatCard
                    icon={TrendingUp}
                    label={t('groups.detailPage.activity')}
                    value={
                      !canViewPosts
                        ? '—'
                        : postsCount >= 20
                        ? t('groups.activityLevels.high')
                        : postsCount >= 5
                        ? t('groups.activityLevels.moderate')
                        : postsCount >= 1
                        ? t('groups.activityLevels.low')
                        : t('groups.activityLevels.new')
                    }
                    color="purple"
                  />
                  <StatCard
                    icon={Shield}
                    label={t('groups.detailPage.privacy')}
                    value={group?.privacy === 'public' ? t('groups.detailPage.public') : t('groups.detailPage.private')}
                    color="yellow"
                  />
                </div>

                {/* Tabs -- scrollable row on mobile rather than clipping/overflowing;
                    each TabButton is flex-shrink-0 (see TabButton.jsx) so it keeps
                    its full label instead of getting squished */}
                <div className="flex space-x-2 mb-8 bg-cream-100 dark:bg-gray-800/30 p-2 rounded-2xl border border-cream-300 dark:border-gray-700/50 overflow-x-auto">
                  <TabButton
                    active={activeTab === 'posts'}
                    onClick={() => canViewPosts && setActiveTab('posts')}
                    icon={MessageCircle}
                    count={canViewPosts ? postsCount : null}
                    disabled={!canViewPosts}
                  >
                    {t('groups.detailPage.tabPosts')}
                  </TabButton>
                  <TabButton
                    active={activeTab === 'members'}
                    onClick={() => canViewMembers && setActiveTab('members')}
                    icon={Users}
                    count={canViewMembers ? approvedMembers.length : null}
                    disabled={!canViewMembers}
                  >
                    {t('groups.detailPage.tabMembers')}
                  </TabButton>
                  <TabButton
                    active={activeTab === 'about'}
                    onClick={() => setActiveTab('about')}
                    icon={Shield}
                  >
                    {t('groups.detailPage.tabAbout')}
                  </TabButton>
                  <TabButton
                    active={activeTab === 'stocks'}
                    onClick={() => setActiveTab('stocks')}
                    icon={TrendingUp}
                  >
                    {t('groups.detailPage.tabStocks')}
                  </TabButton>
                  <TabButton
                    active={activeTab === 'pricing'}
                    onClick={() => setActiveTab('pricing')}
                    icon={DollarSign}
                  >
                    {t('groups.detailPage.tabPricing')}
                  </TabButton>
                  <TabButton
                    active={activeTab === 'analytics'}
                    onClick={() => setActiveTab('analytics')}
                    icon={BarChart3}
                  >
                    {t('groups.detailPage.tabAnalytics')}
                  </TabButton>
                </div>

                {/* Panels (persisted, hidden/shown) */}
                <div className="bg-cream-100 dark:bg-gray-800/50 rounded-2xl border border-cream-300 dark:border-gray-700/50 overflow-hidden">
                  {/* Posts */}
                  <div className={activeTab === 'posts' ? 'block p-4 sm:p-8' : 'hidden'}>
                    {isMember ? (
                      <GroupPosts
                        groupId={groupId}
                        currentUser={currentUser}
                        isMember={isMember}
                        canCreatePost={canCreatePost}
                        isMuted={isMemberMuted}
                        mutedUntil={myMembership?.muted_until}
                        posts={posts}
                        hasMore={postsHasMore}
                        status={postsStatus}
                        error={postsError}
                        onRetry={onRetryPosts}
                        onLoadMore={onLoadMorePosts}
                        onCreatePost={onCreatePost}
                        groupStocks={groupStocks}  // ✅ pass tagged stocks into composer
                        onLike={onLike}
                        onUnlike={onUnlike}
                        onBull={onBull}
                        onUnbull={onUnbull}
                        onBear={onBear}
                        onUnbear={onUnbear}
                        onCreateComment={onCreateComment}
                        onDeleteComment={onDeleteComment}
                        onLikeComment={onLikeComment}
                        onUnlikeComment={onUnlikeComment}
                        onSave={onSave}
                        onUnsave={onUnsave}
                        savedGroupPostIds={savedGroupPostIds}
                      />
                    ) : (
                      <div className="text-center py-12">
                        <Shield className="mx-auto text-gray-400 dark:text-gray-500 mb-4" size={48} />
                        <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">{t('groups.detailPage.joinToViewPosts')}</h3>
                        <p className="text-gray-500 dark:text-gray-500">{t('groups.detailPage.mustBeMemberToViewPosts')}</p>
                      </div>
                    )}
                  </div>

                  {/* Members */}
                  <div className={activeTab === 'members' ? 'block p-4 sm:p-8' : 'hidden'}>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{t('groups.detailPage.membersHeading')}</h2>
                      <PermissionGate isAdmin={isAdmin} requireAdmin>
                        <button
                          onClick={() => setShowInviteModal(true)}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl flex items-center space-x-2 transition-all duration-300"
                        >
                          <UserPlus size={16} />
                          <span>{t('groups.detailPage.invite')}</span>
                        </button>
                      </PermissionGate>
                    </div>

                    {canViewMembers ? (
                      <>
                        {isAdmin && (
                          <GroupPendingMembers
                            pendingMembers={pendingMembers}
                            groupId={groupId}
                            approveMemberError={approveMemberError}
                            rejectMemberError={rejectMemberError}
                          />
                        )}
                        {/* muteError: mute/unmute are dispatched from this
                            page directly (onMuteMember/onUnmuteMember below),
                            not inside MemberList -- same "captured but never
                            rendered" gap as the pending-member errors above. */}
                        {muteError && (
                          <p className="text-sm text-red-500 dark:text-red-400 mb-3">{muteError}</p>
                        )}
                        <MemberList
                          members={approvedMembers}
                          currentUser={currentUser}
                          isOwner={isOwner}
                          isAdmin={isAdmin}
                          canMute={canMute}
                          viewerRole={viewerRole}
                          isLoading={fetchMembersStatus === 'loading'}
                          onAddMember={() => setShowInviteModal(true)}
                          onRemoveMember={onRemoveMember}
                          onPromoteMember={onPromoteMember}
                          onDemoteMember={onDemoteMember}
                          onTransferOwnership={onTransferOwnership}
                          onMuteMember={onMuteMember}
                          onUnmuteMember={onUnmuteMember}
                        />
                      </>
                    ) : (
                      <div className="text-center py-12 text-gray-500 dark:text-gray-500">
                        {t('groups.detailPage.membersHiddenForNonMembers')}
                      </div>
                    )}
                  </div>

                  {/* About */}
                  <div className={activeTab === 'about' ? 'block p-4 sm:p-8' : 'hidden'}>
                    <GroupAbout
                      group={group}
                      onUpdateGroup={onUpdateGroup}
                      isUpdating={updateGroupStatus === 'loading'}
                      currentUser={currentUser}
                      isOwner={isOwner}
                      postsCount={canViewPosts ? postsCount : null}
                      avatarUrl={displayPicture?.picture_url}
                      onSelectAvatar={onSelectGroupAvatar}
                      canEditGroupTheme={canEditGroupTheme}
                      onSelectBackgroundTheme={onSelectGroupBackgroundTheme}
                      isSavingTheme={updateThemeStatus === 'loading'}
                    />
                  </div>

                  {/* Stocks */}
                  <div className={activeTab === 'stocks' ? 'block p-4 sm:p-8' : 'hidden'}>
                    <GroupStocksSection
                      groupId={groupId}
                      isOwner={isOwner}
                      existingStocks={groupStocks}
                      onStockAssociationComplete={handleStockAssociationComplete}
                    />
                  </div>

                  {/* Pricing */}
                  <div className={activeTab === 'pricing' ? 'block p-4 sm:p-8' : 'hidden'}>
                    <GroupPricing groupId={groupId} isOwner={isOwner} />
                  </div>

                  {/* Analytics */}
                  <div className={activeTab === 'analytics' ? 'block p-4 sm:p-8' : 'hidden'}>
                    {activeTab === 'analytics' && <GroupAnalytics groupId={groupId} />}
                  </div>
                </div>

                {/* Sidebar cards */}
                <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-cream-100 dark:bg-gray-800/50 rounded-2xl p-6 border border-cream-300 dark:border-gray-700/50">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                      <Clock size={20} className="text-blue-400" />
                      <span>{t('groups.detailPage.recentActivity')}</span>
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                          <Users size={16} className="text-white" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-600 dark:text-gray-300">{t('groups.detailPage.groupCreated')}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-500">
                            {new Date(group?.created_at || Date.now()).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-cream-100 dark:bg-gray-800/50 rounded-2xl p-6 border border-cream-300 dark:border-gray-700/50">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                      <Shield size={20} className="text-green-400" />
                      <span>{t('groups.detailPage.groupGuidelines')}</span>
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                      {group?.bio || t('groups.detailPage.noGuidelinesSet')}
                    </p>
                  </div>
                </div>

                {/* Danger Zone */}
                <PermissionGate isOwner={isOwner} requireOwner>
                  <div className="mt-8 bg-red-50 dark:bg-red-900/10 rounded-2xl p-6 border border-red-200 dark:border-red-500/20">
                    <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4 flex items-center space-x-2">
                      <Shield size={20} />
                      <span>{t('groups.detailPage.dangerZone')}</span>
                    </h3>
                    <div className="space-y-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('groups.detailPage.dangerZoneBody')}
                      </p>
                      <button
                        onClick={() => setShowDeleteModal(true)}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl flex items-center space-x-2 transition-all duration-300"
                      >
                        <Trash2 size={16} />
                        <span>{t('groups.detailPage.deleteGroup')}</span>
                      </button>
                    </div>
                  </div>
                </PermissionGate>
              </div>
            </div>
          </div>
        </div>

      {/* Modals */}
      <ReportModal
        isOpen={showReportModal}
        onClose={() => setShowReportModal(false)}
        target={group}
        type="group"
      />

      <DeleteGroupModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={onDeleteGroup}
        groupName={group?.name || ''}
        isDeleting={deleteGroupStatus === 'loading'}
      />

      <InviteMembersModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        groupId={groupId}
        existingMembers={members}
        onInvited={() => dispatch(fetchGroupMembers(groupId))}
      />

      {/* Join confirmation -- previously silent, the panel just disappeared
          once membership flipped with no acknowledgement at all. */}
      <div
        className={`fixed top-20 right-4 z-[100] transition-all duration-300 ${
          showJoinedToast ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-3 pointer-events-none'
        }`}
        role="status"
        aria-live="polite"
      >
        <div className="flex items-center gap-3 rounded-2xl border-2 border-transparent bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-xl px-4 py-3">
          <CheckCircle className="w-6 h-6 text-white flex-shrink-0" />
          <div>
            <p className="text-sm font-bold text-white">{t('groups.detailPage.youreIn')}</p>
            <p className="text-xs text-white/80">{t('groups.detailPage.welcomeToGroup', { groupName: group?.name || t('groups.detailPage.theGroupFallback') })}</p>
          </div>
        </div>
      </div>
    </div>
  );

};

export default GroupDetailPage;